const toLocalISODate = (date: Date) =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${
    date.getDate()
  }`.padStart(2, "0")}`;

const keyFromParts = (year: number, month: number, day: number) =>
  `${year}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;

const keyFromDateUTC = (date: Date) =>
  keyFromParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());

export const parseDateKeyUTC = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year || 1970, (month || 1) - 1, day || 1));
};

export const isValidDateKey = (value?: string): value is string =>
  Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && keyFromDateUTC(parseDateKeyUTC(value)) === value);

export const isCompleteDateRange = (start?: string, end?: string) =>
  isValidDateKey(start) && isValidDateKey(end) && start <= end;

export const addDaysToDateKey = (key: string, days: number) => {
  const date = parseDateKeyUTC(key);
  date.setUTCDate(date.getUTCDate() + days);
  return keyFromDateUTC(date);
};

export const addMonthsToDateKey = (
  key: string,
  months: number,
  dayOfMonth = 1
) => {
  const date = parseDateKeyUTC(key);
  date.setUTCMonth(date.getUTCMonth() + months, dayOfMonth);
  return keyFromDateUTC(date);
};

export const monthEndDateKey = (key: string) => addMonthsToDateKey(key, 1, 0);

export const toDateKeyInTimezone = (value?: string | null, timeZone?: string) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  if (!timeZone) return toLocalISODate(d);
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    if (!year || !month || !day) return null;
    return `${year}-${month}-${day}`;
  } catch {
    return toLocalISODate(d);
  }
};

export const toDateKey = (value?: string | null) => toDateKeyInTimezone(value);

export const nowKeyInTimezone = (timeZone?: string) =>
  toDateKeyInTimezone(new Date().toISOString(), timeZone) ||
  toLocalISODate(new Date());

export type DateOnlyRange =
  | "today"
  | "yesterday"
  | "this_week"
  | "last_week"
  | "last_2_weeks"
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "this_year"
  | "last_year"
  | "all_time"
  | "custom";

export type DateRangeOption = { value: DateOnlyRange; label: string };
export type DateRangePreset = Exclude<DateOnlyRange, "custom">;

export const DATE_RANGE_OPTIONS: readonly DateRangeOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_2_weeks", label: "Last 2 Weeks" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "all_time", label: "All Time" },
  { value: "custom", label: "Custom" },
];

export const DATE_RANGE_PRESET_OPTIONS = DATE_RANGE_OPTIONS.filter(
  (option): option is { value: DateRangePreset; label: string } =>
    option.value !== "custom"
);

export const isDateOnlyRange = (value: string): value is DateOnlyRange =>
  DATE_RANGE_OPTIONS.some((option) => option.value === value);

export function dateOnlyRangeBounds(
  range: DateOnlyRange,
  custom: { startDate?: string; endDate?: string } = {},
  today = nowKeyInTimezone()
): { startDate?: string; endDate?: string } {
  if (range === "all_time") return {};
  if (range === "custom") {
    if (
      isCompleteDateRange(custom.startDate, custom.endDate)
    ) {
      return { startDate: custom.startDate, endDate: custom.endDate };
    }
    const safeDate =
      isValidDateKey(custom.startDate) && !custom.endDate
        ? custom.startDate
        : isValidDateKey(custom.endDate) && !custom.startDate
          ? custom.endDate
          : today;
    return { startDate: safeDate, endDate: safeDate };
  }
  if (range === "today") return { startDate: today, endDate: today };
  if (range === "yesterday") {
    const date = addDaysToDateKey(today, -1);
    return { startDate: date, endDate: date };
  }
  if (range === "this_week" || range === "last_week" || range === "last_2_weeks") {
    const offsetToMonday = (parseDateKeyUTC(today).getUTCDay() + 6) % 7;
    const startDate = addDaysToDateKey(today, -offsetToMonday - (range === "this_week" ? 0 : 7));
    return { startDate, endDate: addDaysToDateKey(startDate, range === "last_2_weeks" ? 13 : 6) };
  }
  if (range === "this_month" || range === "last_month") {
    const startDate = addMonthsToDateKey(today, range === "last_month" ? -1 : 0, 1);
    return { startDate, endDate: monthEndDateKey(startDate) };
  }
  if (range === "last_3_months" || range === "last_6_months") {
    const startDate = addMonthsToDateKey(today, range === "last_3_months" ? -2 : -5, 1);
    return { startDate, endDate: monthEndDateKey(addMonthsToDateKey(today, 0, 1)) };
  }
  const year = parseDateKeyUTC(today).getUTCFullYear() - (range === "last_year" ? 1 : 0);
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

const timezoneOffsetMs = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const year = Number(parts.find((part) => part.type === "year")?.value || 0);
  const month = Number(parts.find((part) => part.type === "month")?.value || 1);
  const day = Number(parts.find((part) => part.type === "day")?.value || 1);
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  const second = Number(parts.find((part) => part.type === "second")?.value || 0);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

const zonedDateTimeToUtcIso = (
  key: string,
  timeZone: string,
  endOfDay = false,
  time?: string
) => {
  const [year, month, day] = key.split("-").map(Number);
  const hour = time ? Number(time.split(":")[0]) : endOfDay ? 23 : 0;
  const minute = time ? Number(time.split(":")[1]) : endOfDay ? 59 : 0;
  const second = endOfDay ? 59 : 0;
  const ms = endOfDay ? 999 : 0;
  const naiveUtc = Date.UTC(
    year || 1970,
    (month || 1) - 1,
    day || 1,
    hour,
    minute,
    second,
    ms
  );
  const firstOffset = timezoneOffsetMs(new Date(naiveUtc), timeZone);
  let adjusted = naiveUtc - firstOffset;
  const secondOffset = timezoneOffsetMs(new Date(adjusted), timeZone);
  if (secondOffset !== firstOffset) {
    adjusted = naiveUtc - secondOffset;
  }
  return new Date(adjusted).toISOString();
};

export function dateKeyBoundsToIso(
  startKey: string,
  endKey: string,
  timeZone?: string
): { startIso: string; endIso: string } {
  if (!timeZone) {
    const start = new Date(`${startKey}T00:00:00`);
    const end = new Date(`${endKey}T23:59:59.999`);
    return {
      startIso: start.toISOString(),
      endIso: end.toISOString(),
    };
  }
  try {
    return {
      startIso: zonedDateTimeToUtcIso(startKey, timeZone, false),
      endIso: zonedDateTimeToUtcIso(endKey, timeZone, true),
    };
  } catch {
    const start = new Date(`${startKey}T00:00:00`);
    const end = new Date(`${endKey}T23:59:59.999`);
    return {
      startIso: start.toISOString(),
      endIso: end.toISOString(),
    };
  }
}

export function isValidTimeOfDay(value: string, allowEndOfDay = false): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) || (allowEndOfDay && value === "24:00");
}

export function dateTimeInTimezoneToIso(day: string, time: string, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone): string {
  if (!isValidDateKey(day) || !isValidTimeOfDay(time)) throw new Error("Enter a valid date and time.");
  const iso = zonedDateTimeToUtcIso(day, timeZone, false, time);
  const displayed = new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  if (toDateKeyInTimezone(iso, timeZone) !== day || displayed !== time) throw new Error("This time does not exist on that date because the clocks change. Choose another time.");
  return iso;
}
